import React, {useState, useEffect, useRef} from 'react';
import {
    getBeneficiaryPPAList,
    getExportBeneficiaryPPAList,
    updateSettlementRecords,
    getBeneficiaryPPATrail,
} from '../../helper/Api';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { DEFAULT_EXPENSE_DATE_BASIS } from '../../RTK/Slices/AppConfigSlice';
import { toast } from 'react-toastify';
import Spinner from '../../components/Tools/spinner';
import CustomBreadcrumbs from '../../components/Tools/Breadcumbs';
import SubHeader from '../../components/Subheader/Subheader';
import MainFilter from '../../components/Tools/MainFilter';
import DashboardFilter from '../Dashboard/components/DashboardFilter';
import { AddCommasToAmount, downloadAuthBlobResponse, getBlobErrorMessage } from '../../helper/Utils';
import { openPpaDocumentOrToast } from '../../helper/secureDocuments';
import PpaDeductionDetailsCell from '../../components/Ppa/PpaDeductionDetailsCell';
import PpaSchemeInfoCell from '../../components/Ppa/PpaSchemeInfoCell';
import {
    PpaSchemeInfoExpandProvider,
    PpaSchemeInfoColumnHeader,
} from '../../components/Ppa/PpaSchemeInfoExpandContext';
import DepartmentIdentityCell from '../../components/Tools/DepartmentIdentityCell';
import moment from "moment";
import { AiOutlineVerticalAlignBottom } from 'react-icons/ai';
import CloseIcon from '@mui/icons-material/Close';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import {
    Box,
    Button,
    IconButton,
    Modal,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from '@mui/material';

const ACTION_ICON_GAP = 6;
const ACTION_TWO_ICON_ROW_WIDTH = 62;
const ACTION_COLUMN_WIDTH = '70px';
const DEDUCTION_COLUMN_WIDTH = '170px';

/** R = reimbursement, D = invoice, S = salary (salary display parked for now). */
const getPpaRecordType = (row) => {
    const prefix = String(row?.ppa_no || '').trim().charAt(0).toUpperCase();
    if (prefix === 'R' || row?.beneficiary_reimbursement_id) {
        return 'reimbursement';
    }
    if (prefix === 'S' || row?.salary_id) {
        return 'salary';
    }
    return 'invoice';
};

const getVoucherOrderDisplay = (row) => {
    const type = getPpaRecordType(row);
    if (type === 'reimbursement') {
        return row?.beneficiary_reimbursement_id || '-';
    }
    if (type === 'salary') {
        return row?.salary_id || '-';
    }
    return row?.invoice_id || row?.voucher_order_no || '-';
};

const getBeneficiaryDisplay = (row) => {
    const type = getPpaRecordType(row);

    if (type === 'reimbursement') {
        return {
            name: row?.reimbursement_beneficiary_name || '-',
            account: row?.reimbursement_beneficiary_account_no || '-',
            ifsc: row?.reimbursement_beneficiary_ifsc_code || '-',
        };
    }

    if (type === 'salary') {
        return {
            name: '-',
            account: '-',
            ifsc: '-',
        };
    }

    return {
        name: row?.invoice_beneficiary_name || row?.beneficiary_name || '-',
        account: row?.invoice_beneficiary_account_no || row?.account_no || '-',
        ifsc: row?.invoice_beneficiary_ifsc_code || row?.ifsc_code || '-',
    };
};

const formatTableAmount = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return AddCommasToAmount(value) || '-';
};

export default function BeneficiaryPpa() {
    const { authData, financialYears } = useSelector((state) => state.authData);
    const expenseDateBasis = useSelector(
        (state) => state.appConfig?.expenseDateBasis || DEFAULT_EXPENSE_DATE_BASIS
    );
    const fyList = financialYears.payload.fy_list;
    const fyIdRef = useRef(localStorage.getItem('globalDateId'));
    const [fyId, setFyId] = useState(fyIdRef.current);
    const [data, setData] = useState([]);
    const [dataLength, setDataLength] = useState(0);
    const location = useLocation();
    const [filter, setFilter] = useState('own');
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [openModal, setOpenModal] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState([]);
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const [filterPayload, setFilterPayload] = useState({
        searchStr: '',
        fromDate: '',
        toDate: '',
        dateColumn: '',
        department_id: '',
        tier: '',
        consolidate: false,
        district_id: '',
        district_name: '',
    });

    const [filterApplied, setFilterApplied] = useState({
        searchStr: '',
        fromDate: '',
        toDate: '',
        dateColumn: '',
        department_id: '',
        tier: '',
        consolidate: false,
        district_id: '',
        district_name: '',
    });

    const pageRef = useRef({
        start: 0,
        end: 0,
        currentPage: 1,
    });

    const from_date = filterApplied.fromDate || '';
    const to_date = filterApplied.toDate || '';

    const handleInput = async (e) => {
        const { name, value } = e.target;
        setFilterPayload((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const bootList = async () => {
        setPage(0);
        setFilterApplied(filterPayload);
        setOpen(false);
    };

    const handleFilterRemove = async (name) => {
        let newPayload;
        if (name === 'fromDate' || name === 'toDate') {
            newPayload = {
                ...filterPayload,
                fromDate: '',
                toDate: '',
            };
        } else if (name === 'district_id') {
            newPayload = {
                ...filterPayload,
                district_id: '',
                district_name: '',
            };
        } else {
            newPayload = {
                ...filterPayload,
                [name]: '',
            };
        }
        setFilterPayload(newPayload);
        setFilterApplied(newPayload);
        setPage(0);
    };

    const handlePageTrigger = () => {
        fyIdRef.current = localStorage.getItem('globalDateId');
        setFyId(fyIdRef.current);
        setPage(0);
    };

    const [, setAmountFormatTick] = useState(0);

    useEffect(() => {
        const onAmountDivisorChange = () => setAmountFormatTick((tick) => tick + 1);
        window.addEventListener('amount_divisor_changed', onAmountDivisorChange);
        return () => window.removeEventListener('amount_divisor_changed', onAmountDivisorChange);
    }, []);

    // Same column layout as ConsolidateBeneficiaryPpa.jsx (single-department data is mapped in renderCellValue)
    const columns = [
        { Header: '#', accessor: 'sno', style: { width: '70px' } },
        { Header: 'Actions', accessor: 'ppa_url', style: { width: ACTION_COLUMN_WIDTH } },
        { Header: 'PPA Info', accessor: 'ppa_info', style: { width: '200px' } },
        { Header: 'Department (Tier / Code)', accessor: 'department_info', style: { width: '250px' } },
        { Header: <PpaSchemeInfoColumnHeader />, accessor: 'scheme_info', style: { width: '250px' } },
        { Header: 'voucher / order No', accessor: 'invoice_id', style: { width: '200px' } },
        { Header: 'Beneficiary Info', accessor: 'beneficiary_info', style: { width: '220px' } },
        { Header: 'Amount Info', accessor: 'amount_info', style: { width: '200px' } },
        { Header: 'Deduction Details', accessor: 'deduction_info', style: { width: DEDUCTION_COLUMN_WIDTH } },
    ];

    const isWrapColumn = (accessor) => accessor === 'scheme_info' || accessor === 'deduction_info';
    const isActionsColumn = (accessor) => accessor === 'ppa_url';
    const getColumnCellStyle = (column) => ({
        minWidth: column?.style?.width || column.minWidth,
        width: column?.style?.width,
        maxWidth: column?.style?.width,
        whiteSpace: isWrapColumn(column.accessor) ? 'normal' : 'nowrap',
        overflow: isActionsColumn(column.accessor) ? 'visible' : 'hidden',
        textOverflow: isWrapColumn(column.accessor) ? 'clip' : 'ellipsis',
        wordBreak: isWrapColumn(column.accessor) ? 'break-word' : 'normal',
        ...(isActionsColumn(column.accessor) ? { padding: '8px 4px' } : {}),
        ...(column.style || {}),
    });

    const downloadPPAFun = async (url, ppa_id) => {
        await openPpaDocumentOrToast(ppa_id, url);
    };

    const fetchExcelRecords = async () => {
        try {
            setLoading(true);
            const resp = await getExportBeneficiaryPPAList({
                from_date,
                to_date,
                search_str: '',
                department_id: filterApplied.department_id || authData.user.department_id,
                fy_id: fyId || localStorage.getItem('globalDateId') || '',
                expense_date_basis: expenseDateBasis,
            });
            await downloadAuthBlobResponse(resp, 'beneficiary_ppa.xlsx');
            setLoading(false);
        } catch (error) {
            setLoading(false);
            toast.error(await getBlobErrorMessage(error, 'Failed to export'), {
                position: toast.POSITION.TOP_CENTER,
            });
        }
    };

    const loadRecords = async (limit, offset) => {
        await updateSettlementRecords();
        await fetchRecords(limit, offset);
    };

    const fetchRecords = async (limit, offset) => {
        try {
            setLoading(true);
            const resp = await getBeneficiaryPPAList({
                limit,
                offset,
                from_date,
                to_date,
                search_str: '',
                department_id: filterApplied.department_id || '',
                fy_id: fyId || localStorage.getItem('globalDateId') || '',
                expense_date_basis: expenseDateBasis,
                district_id: filterApplied.district_id || '',
            });
            if (resp.data.status) {
                setData(resp.data.result.data);
                setDataLength(Number(resp.data.result.total_count) || 0);
                pageRef.current.currentPage = offset;
                pageRef.current.start = (offset - 1) * limit;
                if(resp.data.result.data.length) {
                    pageRef.current.start += 1;
                }
                pageRef.current.end = (offset * limit) > resp.data.result.total_count ? resp.data.result.total_count : (offset * limit);
            }
            setLoading(false);
        } catch (error) {
            setData([]);
            setLoading(false);
            toast.error(error?.message || 'Failed to fetch records', {
                position: toast.POSITION.TOP_CENTER,
            });
        }
    }

    const fetchPaymentTrail = async (ppa_invoice_batch_id, account_no) => {
        try {
            setLoading(true);
            const response = await getBeneficiaryPPATrail({ ppa_invoice_batch_id, account_no });
            if (response && response.data) {
                setSelectedRowData(response.data.result || []);
            }
        } catch (error) {
            toast.error(error?.message || 'Failed to fetch payment trail', {
                position: toast.POSITION.TOP_CENTER,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = async (rowData) => {
        await fetchPaymentTrail(
            rowData.ppa_invoice_batch_id,
            rowData.reimbursement_beneficiary_account_no || rowData.account_no
        );
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedRowData([]);
    };

    const getPaymentStatus = (payment_mode, flag_success_return) => {
        if (payment_mode === 'online') {
            if (flag_success_return === '1') {
                return { label: 'Success', color: '#d4edda', textColor: '#155724' };
            } else if (flag_success_return === '0') {
                return { label: 'Rejected', color: '#f8d7da', textColor: '#721c24' };
            } else if (flag_success_return === null) {
                return { label: 'Pending', color: '#fff3cd', textColor: '#856404' };
            } else if (flag_success_return === '2') {
                return { label: 'Failed', color: '#f8d7da', textColor: '#721c24' };
            } else if (flag_success_return === '3') {
                return { label: 'On Hold', color: '#f8d7da', textColor: '#856404' };
            } else {
                return { label: 'Sent To Bank', color: '#fff3cd', textColor: '#856404' };
            }
        } else {
            return { label: 'Success', color: '#d4edda', textColor: '#155724' };
        }
    };

    const getPaymentModeChip = (paymentMode) => {
        const mode = String(paymentMode || '').toLowerCase();

        if (mode === 'online') {
            return { label: 'Online', bg: '#e8f0ff', color: '#1f4db8' };
        }

        return { label: 'Offline', bg: '#e6f4ea', color: '#1e7e34' };
    };

    const totalColumnWidth = columns.reduce((total, column) => {
        const columnWidth = parseInt(column?.style?.width, 10) || 0;
        return total + columnWidth;
    }, 0);

    const renderCellValue = (row, column, rowIndex) => {
        if (column.accessor === 'sno') {
            return page * rowsPerPage + rowIndex + 1;
        }

        if (column.accessor === 'ppa_url') {
            return (
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: `${ACTION_ICON_GAP}px`,
                        width: `${ACTION_TWO_ICON_ROW_WIDTH}px`,
                        maxWidth: `${ACTION_TWO_ICON_ROW_WIDTH}px`,
                    }}
                >
                    <Tooltip title="Download PPA" arrow>
                        <IconButton
                            size="small"
                            onClick={() => downloadPPAFun(row.ppa_url, row.id || row.ppa_no)}
                            sx={{
                                width: 28,
                                height: 28,
                                border: '1px solid #1976d2',
                                color: '#1976d2',
                                borderRadius: '6px',
                            }}
                        >
                            <AiOutlineVerticalAlignBottom size={16} />
                        </IconButton>
                    </Tooltip>
                    {String(row.payment_mode || '').toLowerCase() === 'online' && (
                        <Tooltip title="Payment Transfer Logs" arrow>
                            <IconButton
                                size="small"
                                onClick={() => handleOpenModal(row)}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    border: '1px solid #f59e0b',
                                    color: '#f59e0b',
                                    borderRadius: '6px',
                                }}
                            >
                                <HistoryOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            );
        }

        if (column.accessor === 'department_info') {
            return (
                <DepartmentIdentityCell
                    row={row}
                    fallbackDistrictRow={authData?.user?.department_details}
                />
            );
        }

        if (column.accessor === 'scheme_info') {
            return <PpaSchemeInfoCell row={row} />;
        }

        if (column.accessor === 'ppa_info') {
            const modeChip = getPaymentModeChip(row?.payment_mode);
            const statusMeta = getPaymentStatus(row.payment_mode, row.flag_success_return);
            const approvalDate = row?.l3_approval_datetime
                ? moment(row.l3_approval_datetime, [
                    'YYYY-MM-DD HH:mm:ss',
                    'YYYY-MM-DDTHH:mm:ssZ',
                    'YYYY-MM-DDTHH:mm:ss.SSSZ',
                    moment.ISO_8601,
                ]).format('DD MMM YYYY, hh:mm A')
                : '-';

            return (
                <div style={{ lineHeight: 1.25 }}>
                    <div style={{ fontWeight: 600 }}>PPA: {row?.ppa_no || '-'}</div>
                    <div style={{ marginTop: '4px' }}>
                        <span
                            style={{
                                padding: '3px 8px',
                                borderRadius: '999px',
                                backgroundColor: modeChip.bg,
                                color: modeChip.color,
                                fontSize: '12px',
                                fontWeight: 600,
                                display: 'inline-block',
                            }}
                        >
                            {modeChip.label}
                        </span>
                    </div>
                    <div style={{ color: '#6b7280', marginTop: '4px', fontSize: '12px' }}>
                        {approvalDate}
                    </div>
                    <div style={{ marginTop: '6px' }}>
                        {statusMeta ? (
                            <span
                                style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: statusMeta.color,
                                    color: statusMeta.textColor,
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    display: 'inline-block',
                                }}
                            >
                                {statusMeta.label}
                            </span>
                        ) : (
                            <span style={{ color: '#9ca3af', fontSize: '12px' }}>N/A</span>
                        )}
                    </div>
                </div>
            );
        }

        if (column.accessor === 'invoice_id') {
            return getVoucherOrderDisplay(row);
        }

        if (column.accessor === 'beneficiary_info') {
            const { name, account, ifsc } = getBeneficiaryDisplay(row);
            return (
                <div style={{ lineHeight: 1.25 }}>
                    <div style={{ fontWeight: 600 }}>{name}</div>
                    <div style={{ color: '#6b7280', marginTop: '4px' }}>A/C: {account}</div>
                    <div style={{ color: '#6b7280', marginTop: '2px' }}>IFSC: {ifsc}</div>
                </div>
            );
        }

        if (column.accessor === 'amount_info') {
            const type = getPpaRecordType(row);
            const sanctioned = type === 'reimbursement'
                ? (row?.reimbursement_gross_amount ?? row?.invoice_sanctioned_amount ?? row?.sanctioned_amount)
                : (row?.invoice_sanctioned_amount ?? row?.sanctioned_amount ?? row?.reimbursement_gross_amount);
            const deduction = type === 'reimbursement'
                ? (row?.reimbursement_deduction_amount ?? row?.invoice_total_deduction ?? row?.deduction_amount)
                : (row?.invoice_total_deduction ?? row?.deduction_amount ?? row?.reimbursement_deduction_amount);
            const payment = type === 'reimbursement'
                ? (row?.reimbursement_net_amount ?? row?.invoice_total_payable ?? row?.payment_amount)
                : (row?.invoice_total_payable ?? row?.payment_amount ?? row?.reimbursement_net_amount);
            return (
                <div style={{ lineHeight: 1.25 }}>
                    <div style={{ fontWeight: 600 }}>Sanctioned: {formatTableAmount(sanctioned)}</div>
                    <div style={{ color: '#6b7280', marginTop: '4px' }}>Deduction: {formatTableAmount(deduction)}</div>
                    <div style={{ color: '#6b7280', marginTop: '2px' }}>Payment: {formatTableAmount(payment)}</div>
                </div>
            );
        }

        if (column.accessor === 'deduction_info') {
            return <PpaDeductionDetailsCell row={row} />;
        }

        return row?.[column.accessor] ?? '-';
    };
    
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const newReportType = queryParams.get('reportType') || 'own';
        setFilter(newReportType);
        setPage(0);
    }, [location.search]);

    useEffect(() => {
        if (filter !== 'own') {
            return;
        }
        loadRecords(rowsPerPage, page + 1);
    }, [
        page,
        rowsPerPage,
        filterApplied.department_id,
        filterApplied.fromDate,
        filterApplied.toDate,
        filterApplied.district_id,
        filter,
        fyId,
        expenseDateBasis,
    ]);

    return (
        <div>
            {loading && <Spinner />}
            <SubHeader fy_list={fyList} handlePageTrigger={handlePageTrigger} />
            <div className='scheme p-2'>
                <div className="row" style={{ padding: "10px 16px" }}>
                    <div className="col-md-6">
                        <h4 style={{ color: "#28326f", margin:0}}>Beneficiary PPA Report</h4>
                        <CustomBreadcrumbs linksArray={[]} title="Beneficiary PPA Report"/>
                    </div>
                    <div className="col-md-6">
                        <div className="d-flex gap-2" style={{ width: "auto", float: "right" }}>
                            <Button variant="contained" className="navigation_button" onClick={() => fetchExcelRecords()}> Export Excel</Button>
                        </div>
                    </div>
                </div>
                <DashboardFilter
                    openFilterModal={setOpen}
                    filterPayload={filterApplied}
                    handleFilterRemove={handleFilterRemove}
                    isTierRemovalRequired={false}
                />
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                    <PpaSchemeInfoExpandProvider>
                    <TableContainer sx={{ maxHeight: '72vh', overflowX: 'auto', overflowY: 'auto' }}>
                        <Table
                            stickyHeader
                            aria-label="sticky table"
                            sx={{
                                tableLayout: 'fixed',
                                width: `${totalColumnWidth}px`,
                                minWidth: `${totalColumnWidth}px`,
                            }}
                        >
                            <colgroup>
                                {columns.map((column) => (
                                    <col key={`col_${column.accessor}_${column?.style?.width || 'auto'}`} style={{ width: column?.style?.width }} />
                                ))}
                            </colgroup>
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={`head_${column.accessor}_${column?.style?.width || 'auto'}`}
                                            align={column.align}
                                            style={getColumnCellStyle(column)}
                                        >
                                            {column.label || column.Header}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map((row, rowIndex) => (
                                    <TableRow
                                        hover
                                        role="checkbox"
                                        tabIndex={-1}
                                        key={`row_${row.id || 'noid'}_${row.ppa_no || 'noppa'}_${rowIndex}`}
                                    >
                                        {columns.map((column) => (
                                            <TableCell
                                                key={`cell_${column.accessor}_${row.id || 'noid'}_${row.ppa_no || 'noppa'}_${rowIndex}`}
                                                align={column.align}
                                                style={getColumnCellStyle(column)}
                                            >
                                                {renderCellValue(row, column, rowIndex)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[50, 100, 200, 500]}
                        component="div"
                        count={dataLength || data.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(+event.target.value);
                            setPage(0);
                        }}
                    />
                    </PpaSchemeInfoExpandProvider>
                </Paper>

            {/* Modal for Payment Trail */}
            <Modal
                open={openModal}
                onClose={handleCloseModal}
                aria-labelledby="payment-trail-modal"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: 24,
                        padding: '24px',
                        maxWidth: '900px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h2 id="payment-trail-modal">Payment Trail Details</h2>
                        <Button onClick={handleCloseModal} sx={{ minWidth: 'auto', padding: '0' }}>
                            <CloseIcon />
                        </Button>
                    </Box>

                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #ddd', background: '#000' }}>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Transaction ID</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Date</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Amount</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Status</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Remarks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedRowData.length > 0 ? (
                                selectedRowData.map((trail, index) => (
                                    <tr key={`trail_${trail.transaction_reference || 'notxn'}_${trail.created_at || 'nodate'}_${index}`} style={{ borderBottom: '1px solid #ddd' }}>
                                        <td style={{ padding: '12px' }}>{trail.transaction_reference || 'N/A'}</td>
                                        <td style={{ padding: '12px' }}>{trail.created_at || 'N/A'}</td>
                                        <td style={{ padding: '12px' }}>
                                            {trail.amount_paise != null && trail.amount_paise !== ''
                                                ? formatTableAmount(Number(trail.amount_paise) / 100)
                                                : 'N/A'}
                                        </td>
                                        {index === 0 ? (
                                            <td style={{ padding: '12px' }}>
                                                {getPaymentStatus('online', trail.flag_success_return) ? (
                                                    <span style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        backgroundColor: getPaymentStatus('online', '').color,
                                                        color: getPaymentStatus('online', '').textColor
                                                    }}>
                                                        {getPaymentStatus('online', '').label}
                                                    </span>
                                                ) : (
                                                    'N/A'
                                                )}
                                            </td>
                                        ) : (
                                            <td style={{ padding: '12px' }}>
                                                {getPaymentStatus('online', trail.flag_success_return) ? (
                                                    <span style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        backgroundColor: getPaymentStatus('online', trail.flag_success_return).color,
                                                        color: getPaymentStatus('online', trail.flag_success_return).textColor
                                                    }}>
                                                        {getPaymentStatus('online', trail.flag_success_return).label}
                                                    </span>
                                                ) : (
                                                    'N/A'
                                                )}
                                            </td>
                                        )}
                                        <td style={{ padding: '12px' }}>
                                            {index === 0 ? 'N/A' : trail.reason || 'N/A'}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                                        No payment trail data available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </Box>
            </Modal>
                <MainFilter
                    open={open}
                    handleClose={handleClose}
                    handleInput={handleInput}
                    handleOpen={handleOpen}
                    handleFilterClick={bootList}
                    filterPayload={filterPayload}
                    setConsolidate={setFilterPayload}
                    type="Beneficiary PPA"
                />
            </div>
        </div>
    )
}